<template>
  <v-menu
    v-model="menu"
    max-width="400"
    location="right center"
    :close-on-content-click="false"
  >
    <template v-slot:activator="{ props: activatorProps }">
      <div
        v-bind="activatorProps"
        class="d-flex justify-end selected-bounded-context"
      >
        <v-chip
          size="small"
          density="compact"
          @click="() => {}"
        >
          {{ boundedContextName || 'Select bounded context'}}
        </v-chip>
      </div>
    </template>
    <!-- <template v-slot:default="{ isActive }"> -->
    <template v-slot:default>
      <v-card class="ml-3 pa-3" width="300">
        <div class="d-flex pb-4 justify-space-between">
          <v-btn
            :style="!createBoundedContext ? 'visibility: hidden;' : ''"
            variant="text"
            size="small"
            icon="mdi-arrow-left"
            @click="createBoundedContext = false"
          />
          <span class="text-h7 font-weight-bold pt-2">Bounded Contexts</span>
          <v-btn
            variant="text"
            size="small"
            icon="mdi-close"
            @click="menu = false"
          />
        </div>
        <v-text-field
          v-if="!disabled"
          :rules="[maxNameLength]"
          ref="searchBar"
          class="select-bounded-context"
          autofocus
          density="compact"
          v-model="search"
          :placeholder="!createBoundedContext ? 'Search for bounded contexts' : 'Name your bounded context'"
          variant="outlined"
          @keydown="keyDownBoundedContext">
        </v-text-field>

        <v-list ref="searchList">
          <template v-if="createBoundedContext">
            <v-list-item
              density="compact"
              :disabled="!search"
              @click="updateMenuValue(search)"
            >
              <v-list-item-title>Create</v-list-item-title>
            </v-list-item>
          </template>
          <template v-else>
            <!-- list all bounded contexts -->
            <v-list-item
              v-for="(bc, index) in filteredBoundedContexts"
              density="compact"
              :key="index"
              @click="updateMenuValue(bc.name)"
              :disabled="disabled"
            >
              <v-list-item-title>{{ bc.name }}</v-list-item-title>
            </v-list-item>

            <v-divider class="my-2"></v-divider>

            <v-list-item
              density="compact"
              @click="createBoundedContext = true"
              :disabled="disabled"
            >
              <v-list-item-title>Create a new bounded context</v-list-item-title>
            </v-list-item>
            <v-list-item
              v-if="boundedContextName"
              density="compact"
              :disabled="disabled"
              @click="updateMenuValue('')"
            >
              <v-list-item-title>Detach from bounded context</v-list-item-title>
            </v-list-item>
          </template>
        </v-list>
      </v-card>
    </template>
  </v-menu>
</template>

<script>
import { mapState } from 'vuex'
import workflowApi from '@/api/workflowApi'
import randomUUID from '@/utils/uuid'
import { diagramHelpers } from '@/mixins/diagramHelpers.js'
import safeStructuredClone from '@/utils/clone'
import { SchemaType } from '@/constants/schema'

export default {
  mixins: [diagramHelpers],
  props: [
    'entity',
    'cards',
    'boundedContext'
  ],
  data () {
    return {
      open: false,
      svgGraph: null,
      svgLanes: null,
      refreshKey: 0,
      menu: false,
      keyDownPressed: false,
      search: '',
      createBoundedContext: false
    }
  },
  computed: {
    disabled () {
      return this.$store.state.isWorkflowDisabled
    },
    ...mapState({
      workflow: (state) => state.workflow.workflow
    }),
    boundedContextName () {
      if (this.entity) {
        return (this.workflow.boundedContexts || []).find((it) => it.id === this.entity.boundedContext)?.name || ''
      }

      return this.boundedContext?.name || ''
    },
    filteredBoundedContexts () {
      if (!this.workflow.boundedContexts) {
        return []
      }
      const list = this.workflow.boundedContexts.filter((bc) => bc.name && bc.name !== this.boundedContextName)
      // TODO: improve fuzzy search, was generated by ChatGPT
      return list.filter((bc) => bc.name.toLowerCase().includes(this.search.toLowerCase()))
    }
  },
  methods: {
    maxNameLength (v) {
      return v.length <= 60 || 'Name must be less than 60 characters'
    },
    keyDownBoundedContext (event) {
      if (event.key === 'Enter') {
        event.target.blur()
        this.updateMenuValue(event.target.value)
      }
      if (event.key === 'ArrowDown') {
        this.$refs.searchList?.$el?.firstElementChild?.focus()
        event.stopPropagation()
        event.preventDefault()
      } else if (event.key === 'ArrowUp') {
        this.$refs.searchList?.$el?.lastElementChild?.focus()
        event.stopPropagation()
        event.preventDefault()
      }
    },
    async updateMenuValue (event) {
      this.menu = false

      let boundedContext = (this.workflow.boundedContexts || []).find((it) => it.name === event)
      if (!boundedContext) {
        if (event) {
          boundedContext = await this.createNewBoundedContext(event)
        } else {
          boundedContext = { id: null }
        }
      }

      if (this.entity) {
        await this.updateBoundedContextOnEntity(this.entity, boundedContext.id)
      } else {
        await workflowApi.updateCardsMutation(
          this.$route.params.id,
          this.cards.map((card) => ({
            cardId: card.id,
            updateNote: {
              action: event ? 'attached bounded context' : 'detached from bounded context',
              target: event || this.boundedContextName
            },
            boundedContext: boundedContext.id
          }))
        )
      }
    },
    async createNewBoundedContext (name) {
      const boundedContext = {
        id: randomUUID(),
        name
      }
      await this.$store.dispatch('workflow/updateWorkflow', {
        projectId: this.workflow.projectId,
        input: {
          id: this.workflow.id,
          expectedVersion: this.workflow.version,
          boundedContexts: [...(this.workflow.boundedContexts || []), boundedContext],
          updateNote: {
            action: 'added bounded context',
            target: name
          }
        }
      })
      return boundedContext
    },
    async updateBoundedContextOnEntity (entity, event) {
      if (event) {
        const isDuplicate = (this.workflow.schemas || []).some((s) =>
          s.id !== entity.id &&
          s.type === SchemaType.ENTITY &&
          s.boundedContext === event &&
          s.name.toLowerCase() === entity.name.toLowerCase()
        )

        if (isDuplicate) {
          const bcName = (this.workflow.boundedContexts || []).find((bc) => bc.id === event)?.name || 'this bounded context'
          this.$store.commit('snackbar/showMessage', {
            content: `An entity with the name "${entity.name}" already exists in ${bcName}`,
            timeout: 4000,
            color: 'red'
          }, { root: true })
          return
        }
      }

      const newSchemas = safeStructuredClone(this.workflow.schemas)
      const index = newSchemas.findIndex((model) => model.id === entity.id)
      if (index === -1) {
        return
      }
      newSchemas[index].boundedContext = event

      await workflowApi.updateSchemas({
        schemas: newSchemas,
        workflow: this.workflow,
        updateNote: {
          action: 'updated bounded context on entity',
          target: ''
        }
      })
    }
  },
  watch: {
    createBoundedContext (newValue) {
      if (newValue) {
        this.$refs.searchBar?.focus()
      } else {
        this.search = ''
      }
    },
    menu (newValue) {
      if (newValue) {
        this.search = ''
        this.createBoundedContext = false
      }
    }
  }
}
</script>
